<script lang="ts">
  import {onMount, SvelteComponent} from 'svelte';
  import {filter, take} from 'rxjs/operators';
  import {envUtil} from '@px/helper/env';
  import {sd} from '@px/da/sd';
  import {csRobotUtil} from '@px/da/cs-robot';
  import {timeUtil} from '@px/helper/time';
  import {LG$} from '@px/streams/lang';
  import {authStatus$} from '@px/streams/auth';
  import {getCsRobotInfo, loadChatReady$} from '@px/streams/cs-robot';
  import {url$} from '@px/streams/url';
  import {userTags$, userInfo$, userMemberLevel$} from '@px/streams/user';
  import {lang$} from '@px/streams/lang';
  import {loadCoinInfo, loadCategory} from '@px/streams/config';
  import {updateReferral} from '@px/streams/referral';
  import {isMobileLayout$} from '@px/streams/grid-layout';
  import {innerWidth$, innerHeight$} from '@px/streams/window-inner-size';
  import {isIpPreRestricted$, isIpRestricted$, ipRestrictedConfig$, subPreRestrictedDialog} from '@px/modules/ip-restricted/streams';
  import {announcementTopDis$, marketTopDis$} from '@px/modules/market-select/streams';
  import {clang} from '@px/languages/common.lang';

  const Email = '<a href="mailto:support@phemex.zendesk.com">support@phemex.zendesk.com</a>';

  let isSpot = false;
  let isContract = false;
  let innerWidth: number = 1200;
  let innerHeight: number = 768;
  let canonicalLink = null;
  let Date = '';
  let scrollY = 0;
  let wrapHeight = 0;

  let view: SvelteComponent;

  onMount(() => {
    authStatus$
      .pipe(
        filter((authStatus) => authStatus !== 'Init'),
        take(1)
      )
      .subscribe((authStatus) => sd.pageView({auth_status: authStatus}));
  });

  onMount(() => {
    updateReferral();
  });

  onMount(() => lang$.subscribe((lang) => Promise.all([loadCoinInfo(lang), loadCategory(lang)])));

  onMount(() => {
    subPreRestrictedDialog();
  });

  $: updateView($isMobileLayout$);
  $: updateContext($url$ as Url);
  $: updateInnerSize(innerWidth, innerHeight);
  $: !envUtil.isBot && loadZeSnippet($isMobileLayout$);
  $: updateUserTag($loadChatReady$, $userTags$, $userInfo$, $userMemberLevel$);
  $: Date = timeUtil.formatUTCDate($ipRestrictedConfig$?.startTime);
  $: updateMarketTopDis(wrapHeight, $announcementTopDis$, scrollY);

  function updateContext(url: Url) {
    if (!url) {
      return;
    }
    canonicalLink = url.href;
    isSpot = url.tradeType === 'Spot';
    isContract = url.isPerpetual;
  }

  async function updateView(isMobileLayout) {
    const module = await loadView(isMobileLayout);
    if (module) {
      view = module.default;
    }
  }

  function loadView(isMobileLayout): Promise<{default: SvelteComponent}> {
    return isMobileLayout ? import('./h5.svelte') : import('./pc.svelte');
  }

  function updateInnerSize(innerWidth, innerHeight) {
    innerWidth$.next(innerWidth);
    innerHeight$.next(innerHeight);
  }

  function loadZeSnippet(isMobileLayout) {
    if (isMobileLayout) {
      return;
    }
    setTimeout(async () => {
      const csRobotInfo = await getCsRobotInfo();
      csRobotUtil.init(csRobotInfo, $lang$, function () {
        loadChatReady$.next(true);
      });
    }, 5000);
  }

  function updateUserTag(loadChatReady, userTags, userInfo, userMemberLevel) {
    if (!loadChatReady) {
      return;
    }
    csRobotUtil.changeUserInfo(userInfo, userTags, userMemberLevel);
  }

  function updateMarketTopDis(wrapHeight, announcementTopDis, scrollY) {
    const diff = wrapHeight + announcementTopDis - scrollY;
    marketTopDis$.next(Math.max(diff, 0));
  }
</script>

<style>
  .placeholder-block {
    width: 100%;
    height: 68px;
  }
  .ip-restricted-notify {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 10000;
    box-sizing: border-box;
    padding: 12px 36px;
    width: 100%;
    line-height: 44px;
    background: var(--B2);
    color: var(--T1);
  }
  .error {
    color: #ed0000;
  }
</style>

<svelte:head>
  <link rel="canonical" href={canonicalLink} />
</svelte:head>

<svelte:window bind:innerWidth bind:innerHeight bind:scrollY />

{#if $isIpPreRestricted$ || $isIpRestricted$}
  <div class="placeholder-block" />
  <div class="ip-restricted-notify df" bind:offsetHeight={wrapHeight}>
    <i class="iconfont error f16 mr10">&#xe6c3;</i>
    <div class="f14 lh22">{@html $isIpPreRestricted$ ? $LG$(clang.preRestrictedMsg, {Email, Date}) : $LG$(clang.restrictedMsg, {Email})}</div>
  </div>
{/if}
<svelte:component this={view} {isSpot} {isContract} />
